/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useEffect, useRef, useState, type MemoExoticComponent } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import AiModelIcon from '@atlaskit/icon-lab/core/ai-model';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import Lozenge from '@atlaskit/lozenge';
import { useResizingHeight } from '@atlaskit/motion';
import { Box, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';
import type { StoreMemoryUpdate } from './types';

const styles = cssMap({
	container: {
		marginLeft: token('space.negative.100'),
		marginTop: token('space.150'),
	},
	expandedContent: {
		paddingLeft: token('space.100'),
		color: token('color.text'),
	},
	reviewMemory: {
		textDecoration: 'underline',
		textUnderlineOffset: token('space.025'),
	},
	button: {
		color: token('color.text.subtlest'),
		fontWeight: token('font.weight.bold'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});

export interface StoreMemoryUpdateViewProps {
	storeMemoryUpdate: StoreMemoryUpdate;
	onExpandedChange?: (isExpanded: boolean) => void;
	onManageMemoriesClick?: () => void;
	onOpenMemoryManagement?: () => void;
	onView?: () => void;
	showBetaLabel?: boolean;
}

export const StoreMemoryUpdateView: MemoExoticComponent<
	({
		storeMemoryUpdate,
		onExpandedChange,
		onManageMemoriesClick,
		onOpenMemoryManagement,
		onView,
		showBetaLabel,
	}: StoreMemoryUpdateViewProps) => JSX.Element
> = memo(
	({
		storeMemoryUpdate,
		onExpandedChange,
		onManageMemoriesClick,
		onOpenMemoryManagement,
		onView,
		showBetaLabel = false,
	}: StoreMemoryUpdateViewProps): JSX.Element => {
		const [isExpanded, setIsExpanded] = useState(true);
		const hasTrackedView = useRef(false);
		const useResizingHeightProps = useResizingHeight();
		const { formatMessage } = useIntl();

		useEffect(() => {
			if (!hasTrackedView.current) {
				onView?.();
				hasTrackedView.current = true;
			}
		}, [onView]);

		const handleExpandToggle = () => {
			const nextExpandedState = !isExpanded;
			setIsExpanded(nextExpandedState);
			onExpandedChange?.(nextExpandedState);
		};

		const handleOpenMemoryManagement = () => {
			onManageMemoriesClick?.();
			onOpenMemoryManagement?.();
		};

		return (
			<Stack
				xcss={styles.container}
				alignInline="start"
				space="space.050"
				{...useResizingHeightProps}
			>
				<Pressable xcss={styles.button} onClick={handleExpandToggle}>
					<AiModelIcon label="" color="currentColor" />
					<Text weight="bold" color="color.text.subtlest" size="small">
						{formatMessage(messages.expandButton)}
					</Text>
					{showBetaLabel && (
						<Lozenge appearance="discovery">{formatMessage(messages.betaLabel)}</Lozenge>
					)}
					{isExpanded ? (
						<ChevronDownIcon label="" color="currentColor" size="small" />
					) : (
						<ChevronUpIcon label="" color="currentColor" size="small" />
					)}
				</Pressable>
				<Box>
					{isExpanded && (
						<Stack space="space.050" alignInline="start">
							{storeMemoryUpdate.content && (
								<Box xcss={styles.expandedContent}>
									<Text size="small">{storeMemoryUpdate.content}</Text>
								</Box>
							)}
							<Pressable
								xcss={cx(styles.button, styles.reviewMemory)}
								onClick={handleOpenMemoryManagement}
							>
								<Text weight="regular" size="small">
									{formatMessage(messages.openSettingsButton)}
								</Text>
							</Pressable>
						</Stack>
					)}
				</Box>
			</Stack>
		);
	},
);
